import { ONCHAIN_GC_TIME, ONCHAIN_REFETCH_INTERVAL, ONCHAIN_STALE_TIME } from '@/config/consts'
import { E_EARN_VAULT_CHAIN_ID } from '@/config/euler-contracts'
import { type StrategyAPYData, calculateWeightedAPY } from '@/features/evault-details/logic/calculateWeightedAPY'
import { eEarnVaultImplementationAbi } from 'abis/evm/eEarnVaultImplementation'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { useMemo } from 'react'
import { type Address, getAddress } from 'viem'
import { useReadContracts } from 'wagmi'

interface UseBatchEVaultAPYParams {
  vaultAddresses: Address[]
  enabled?: boolean
}

interface UseBatchEVaultAPYResult {
  apyMap: Map<Address, number>
  /** vault address → deduplicated strategy (lending vault) addresses */
  strategyMap: Map<Address, Address[]>
  isPending: boolean
}

/**
 * Batch fetch and calculate weighted APY for multiple eVaults.
 * Reads both supplyQueue and withdrawQueue to match the detail page calculation.
 */
export function useBatchEVaultAPY({
  vaultAddresses,
  enabled = true,
}: UseBatchEVaultAPYParams): UseBatchEVaultAPYResult {
  // Step 1: Fetch both supply and withdraw queue lengths for each vault
  const queueContracts = useMemo(
    () =>
      vaultAddresses.flatMap((address) => [
        {
          address,
          abi: eEarnVaultImplementationAbi,
          functionName: 'supplyQueueLength' as const,
          chainId: E_EARN_VAULT_CHAIN_ID,
        },
        {
          address,
          abi: eEarnVaultImplementationAbi,
          functionName: 'withdrawQueueLength' as const,
          chainId: E_EARN_VAULT_CHAIN_ID,
        },
      ]),
    [vaultAddresses],
  )

  const { data: queueLengthData, isPending: isPendingQueue } = useReadContracts({
    contracts: queueContracts,
    query: {
      enabled: enabled && vaultAddresses.length > 0,
      staleTime: ONCHAIN_STALE_TIME,
      gcTime: ONCHAIN_GC_TIME,
      refetchInterval: ONCHAIN_REFETCH_INTERVAL,
    },
  })

  // Step 2: Fetch strategy addresses from both supply and withdraw queues
  const strategyContracts = useMemo(() => {
    if (!queueLengthData) return []

    const contracts: any[] = []
    for (let vaultIdx = 0; vaultIdx < vaultAddresses.length; vaultIdx++) {
      const baseIdx = vaultIdx * 2
      const supplyQueueLength = queueLengthData[baseIdx]?.result as bigint | undefined
      const withdrawQueueLength = queueLengthData[baseIdx + 1]?.result as bigint | undefined

      if (supplyQueueLength && supplyQueueLength > 0n) {
        for (let i = 0; i < Number(supplyQueueLength); i++) {
          contracts.push({
            address: vaultAddresses[vaultIdx],
            abi: eEarnVaultImplementationAbi,
            functionName: 'supplyQueue' as const,
            args: [BigInt(i)],
            chainId: E_EARN_VAULT_CHAIN_ID,
            _vaultIdx: vaultIdx,
          })
        }
      }

      if (withdrawQueueLength && withdrawQueueLength > 0n) {
        for (let i = 0; i < Number(withdrawQueueLength); i++) {
          contracts.push({
            address: vaultAddresses[vaultIdx],
            abi: eEarnVaultImplementationAbi,
            functionName: 'withdrawQueue' as const,
            args: [BigInt(i)],
            chainId: E_EARN_VAULT_CHAIN_ID,
            _vaultIdx: vaultIdx,
          })
        }
      }
    }
    return contracts
  }, [queueLengthData, vaultAddresses])

  const { data: strategyAddressData, isPending: isPendingStrategies } = useReadContracts({
    contracts: strategyContracts.map(({ _vaultIdx: _, ...contract }) => contract),
    query: {
      enabled: enabled && strategyContracts.length > 0,
      staleTime: ONCHAIN_STALE_TIME,
      gcTime: ONCHAIN_GC_TIME,
      refetchInterval: ONCHAIN_REFETCH_INTERVAL,
    },
  })

  // Step 3: Build map of vault -> deduplicated strategies
  const vaultStrategiesMap = useMemo(() => {
    const map = new Map<number, Address[]>()
    if (!strategyAddressData) return map

    for (let i = 0; i < strategyContracts.length; i++) {
      const contract = strategyContracts[i]
      const result = strategyAddressData[i]
      if (result?.status === 'success') {
        const vaultIdx = contract._vaultIdx
        const strategyAddr = getAddress(result.result as Address)
        if (!map.has(vaultIdx)) {
          map.set(vaultIdx, [])
        }
        const existing = map.get(vaultIdx)!
        // Deduplicate: a strategy may appear in both supply and withdraw queues
        if (!existing.some((addr) => getAddress(addr) === strategyAddr)) {
          existing.push(strategyAddr)
        }
      }
    }
    return map
  }, [strategyAddressData, strategyContracts])

  // Step 4: Fetch strategy data (allocation, interestRate, totalBorrows, totalAssets, interestFee)
  const strategyDataContracts = useMemo(() => {
    const contracts: any[] = []
    for (const [vaultIdx, strategies] of vaultStrategiesMap.entries()) {
      for (const strategyAddr of strategies) {
        contracts.push(
          {
            address: vaultAddresses[vaultIdx],
            abi: eEarnVaultImplementationAbi,
            functionName: 'config' as const,
            args: [strategyAddr],
            chainId: E_EARN_VAULT_CHAIN_ID,
            _vaultIdx: vaultIdx,
          },
          {
            address: strategyAddr,
            abi: eVaultImplementationAbi,
            functionName: 'interestRate' as const,
            chainId: E_EARN_VAULT_CHAIN_ID,
            _vaultIdx: vaultIdx,
          },
          {
            address: strategyAddr,
            abi: eVaultImplementationAbi,
            functionName: 'totalBorrows' as const,
            chainId: E_EARN_VAULT_CHAIN_ID,
            _vaultIdx: vaultIdx,
          },
          {
            address: strategyAddr,
            abi: eVaultImplementationAbi,
            functionName: 'totalAssets' as const,
            chainId: E_EARN_VAULT_CHAIN_ID,
            _vaultIdx: vaultIdx,
          },
          {
            address: strategyAddr,
            abi: eVaultImplementationAbi,
            functionName: 'interestFee' as const,
            chainId: E_EARN_VAULT_CHAIN_ID,
            _vaultIdx: vaultIdx,
          },
        )
      }
    }
    return contracts
  }, [vaultStrategiesMap, vaultAddresses])

  const { data: strategyData, isPending: isPendingData } = useReadContracts({
    contracts: strategyDataContracts.map(({ _vaultIdx: _, ...contract }) => contract),
    query: {
      enabled: enabled && strategyDataContracts.length > 0,
      staleTime: ONCHAIN_STALE_TIME,
      gcTime: ONCHAIN_GC_TIME,
      refetchInterval: ONCHAIN_REFETCH_INTERVAL,
    },
  })

  // Step 5: Calculate APY for each vault
  const apyMap = useMemo(() => {
    const map = new Map<Address, number>()
    if (!strategyData) return map

    // Group strategy data by vault
    const vaultStrategyData = new Map<number, StrategyAPYData[]>()

    for (let i = 0; i < strategyDataContracts.length; i += 5) {
      const vaultIdx = strategyDataContracts[i]._vaultIdx
      const configResult = strategyData[i]
      const interestRateResult = strategyData[i + 1]
      const totalBorrowsResult = strategyData[i + 2]
      const totalAssetsResult = strategyData[i + 3]
      const interestFeeResult = strategyData[i + 4]

      if (
        configResult?.status === 'success' &&
        interestRateResult?.status === 'success' &&
        totalBorrowsResult?.status === 'success' &&
        totalAssetsResult?.status === 'success'
      ) {
        const config = configResult.result as any
        const shares = config[0] as bigint

        if (!vaultStrategyData.has(vaultIdx)) {
          vaultStrategyData.set(vaultIdx, [])
        }

        vaultStrategyData.get(vaultIdx)!.push({
          shares,
          interestRate: interestRateResult.result as bigint,
          totalBorrows: totalBorrowsResult.result as bigint,
          totalAssets: totalAssetsResult.result as bigint,
          interestFee: interestFeeResult?.status === 'success' ? (interestFeeResult.result as bigint) : undefined,
        })
      }
    }

    // Calculate weighted APY for each vault
    for (const [vaultIdx, strategies] of vaultStrategyData.entries()) {
      const apy = calculateWeightedAPY(strategies)
      const vaultAddress = vaultAddresses[vaultIdx]
      if (vaultAddress) {
        map.set(vaultAddress, apy)
      }
    }

    return map
  }, [strategyData, strategyDataContracts, vaultAddresses])

  // Expose strategy addresses keyed by vault address (for pricing reuse)
  const strategyMap = useMemo(() => {
    const map = new Map<Address, Address[]>()
    for (const [vaultIdx, strategies] of vaultStrategiesMap.entries()) {
      const addr = vaultAddresses[vaultIdx]
      if (addr && strategies.length > 0) {
        map.set(addr, strategies)
      }
    }
    return map
  }, [vaultStrategiesMap, vaultAddresses])

  const isQueuePending = enabled && vaultAddresses.length > 0 && isPendingQueue
  const isStrategiesPending = enabled && strategyContracts.length > 0 && isPendingStrategies
  const isDataPending = enabled && strategyDataContracts.length > 0 && isPendingData
  const isPending = isQueuePending || isStrategiesPending || isDataPending

  return {
    apyMap,
    strategyMap,
    isPending,
  }
}
